// The casino cues of the map feed (audio.md §4.9, R2300): the edges of the
// table frame, and the cues of one result on the animation clock.
//
// THE TABLE FRAME. `tableCues` compares the frame that was with the frame
// that came, which is the rule of `audio/cues.ts`: the reader takes a seat,
// the table deals a card, the reader's own stake moves, the turn comes to
// the reader, the wheel starts to turn. A frame that only restates a state
// sounds nothing.
//
// THE RESULT. The server settles a roulette spin in the tick it enters
// SPINNING, so the result frame lands while the wheel starts to turn. The
// roulette screen holds the called number back until the ball is down
// (`roulette.ts` `ballDown`), and a cue that sounded on the frame gave the
// spin away four seconds early. So every cue of a result waits for the
// moment its picture shows it: the ball cue ends with the drop
// (`CASINO_BALL_CUE_MS`), each reel stop sounds as its reel stops, and the
// verdict waits for `CASINO_WHEEL_MS` or the last reel's stop. Under
// reduced motion the result draws at once: the verdict sounds at once, and
// the ball and the reels, which do not move, sound nothing.
//
// A held cue belongs to its table. When the reader leaves that table, or
// the house dissolves it, the cue is dropped: a verdict that sounds over
// the lobby names a spin nobody can see.
//
// `deps` is the whole of what this module touches outside itself, so the
// test drives the timer with plain functions and no browser.

import {
  CASINO_BALL_CUE_MS, CASINO_JACKPOT_SYMBOL, CASINO_PAYOUT_STAKE_MULT, CASINO_REEL_MS,
  CASINO_REEL_STAGGER_MS,
} from '../../gen/presentation';
import {
  CasinoGame, CasinoPhase, type CasinoResult, type CasinoTable,
} from '../../gen/proto/aquaphobia';
import { casinoSeatOf, casinoSeatsOf } from './casinostate';
import { landingMs } from './landing';

/** True for a slot pull that hit the jackpot line: three reels of the
 *  symbol of `[presentation.casino] jackpot_line` (R3143). balancegen holds
 *  that line to the top of the paytable, and a pull pays the first line
 *  it matches, so the reels name the line that paid. */
export function isJackpot(r: CasinoResult): boolean {
  return r.game === CasinoGame.CASINO_GAME_SLOTS && r.slotPays > 0 && r.reels.length === 3
    && r.reels.every((s) => s === CASINO_JACKPOT_SYMBOL);
}

/**
 * The verdict cue of one result, or null for a result that sounds nothing
 * (audio.md §4.9). A dissolved table gave every stake back, so it is
 * neither a win nor a loss. A win takes the top step it reaches (R3143):
 * the jackpot line, then a move of at least `CASINO_PAYOUT_STAKE_MULT`
 * times the stake, then the plain win.
 */
export function resultCueId(r: CasinoResult): string | null {
  if (r.aborted || r.yourDelta === 0) return null;
  if (r.yourDelta < 0) return 'sfx-casino-lose';
  if (isJackpot(r)) return 'sfx-casino-jackpot';
  if (r.yourStake > 0 && r.yourDelta >= CASINO_PAYOUT_STAKE_MULT * r.yourStake) {
    return 'sfx-casino-payout';
  }
  return 'sfx-casino-win';
}

/** One cue of a result and its wait after the frame landed, in ms. */
export interface CueStep {
  id: string;
  ms: number;
}

/**
 * Every cue of one result, in time order: the ball of a roulette spin,
 * one stop a reel of the slot machine, then the verdict. A dissolved table
 * turned no wheel and pays nothing, so it sounds nothing.
 */
export function resultCuePlan(r: CasinoResult, still: boolean): CueStep[] {
  if (r.aborted) return [];
  const out: CueStep[] = [];
  if (!still && r.game === CasinoGame.CASINO_GAME_ROULETTE) {
    out.push({ id: 'sfx-casino-ball', ms: CASINO_BALL_CUE_MS });
  }
  if (!still && r.game === CasinoGame.CASINO_GAME_SLOTS) {
    // Reel n stops `CASINO_REEL_STAGGER_MS` after reel n-1, the last one at
    // `landingMs` (`slots.ts`).
    for (let i = 0; i < r.reels.length; i++) {
      out.push({ id: 'sfx-casino-reel-stop', ms: CASINO_REEL_MS + CASINO_REEL_STAGGER_MS * i });
    }
  }
  const verdict = resultCueId(r);
  if (verdict !== null) out.push({ id: verdict, ms: landingMs(r, still) });
  return out;
}

/** The sum the reader has staked on the table: every own seat's bet. */
function ownStake(t: CasinoTable): number {
  let sum = 0;
  for (const s of casinoSeatsOf(t)) sum += s.bet;
  return sum;
}

/** The number of cards the reader sees dealt: the board, the own hole
 *  cards, and at blackjack every box and the dealer's hand. A hold'em seat
 *  of another player shows its cards at the showdown alone, which is no
 *  deal, so hold'em counts the board and the own hole cards. */
function dealtCards(t: CasinoTable): number {
  let n = t.board.length + t.yourHole.length;
  if (t.game === CasinoGame.CASINO_GAME_BLACKJACK) {
    for (const s of t.seats) n += s.cards.length;
    n += Math.max(t.dealerCards.length, t.dealerUp !== 0 ? 1 : 0);
  }
  return n;
}

/** True while the turn of the frame is the reader's own. */
function ownTurn(t: CasinoTable): boolean {
  return t.phase === CasinoPhase.CASINO_PHASE_ACTING
    && casinoSeatsOf(t).some((s) => s.seat === t.turnSeat);
}

/** One turn: a new hand, a new street of hold'em or a new seat at turn is
 *  a new turn. Two split hands of one blackjack seat are one turn. */
function turnKey(t: CasinoTable): string {
  return `${t.id}:${t.handId}:${t.turnSeat}:${t.board.length}`;
}

/**
 * The cues of one table frame, from the frame that was (null before the
 * first) to the frame that came (audio.md §4.9):
 *
 * - `sfx-casino-seat`: the reader holds a seat at this table, and held
 *   none at it in the frame before.
 * - `sfx-casino-deal`: the reader sees more dealt cards than before, in one
 *   hand of one table.
 * - `sfx-casino-chip`: the reader's own stake moves in one betting window
 *   (a stake placed or taken back), or rises in one round of acts (a call,
 *   a raise or a double).
 * - `sfx-casino-your-turn`: the turn comes to the reader.
 * - `sfx-casino-wheel`: the frame enters SPINNING.
 *
 * A dissolved table sounds nothing here, and its result sounds nothing.
 */
export function tableCues(was: CasinoTable | null, now: CasinoTable): string[] {
  if (now.phase === CasinoPhase.CASINO_PHASE_DISSOLVED) return [];
  const out: string[] = [];
  const same = was !== null && was.id === now.id;
  if (casinoSeatOf(now) !== null && !(same && casinoSeatOf(was) !== null)) {
    out.push('sfx-casino-seat');
  }
  if (same && was.handId === now.handId) {
    if (dealtCards(now) > dealtCards(was)) out.push('sfx-casino-deal');
    const before = ownStake(was);
    const after = ownStake(now);
    const moved = was.phase !== now.phase ? false
      : now.phase === CasinoPhase.CASINO_PHASE_BETTING ? after !== before
        : now.phase === CasinoPhase.CASINO_PHASE_ACTING
          && was.board.length === now.board.length && after > before;
    if (moved) out.push('sfx-casino-chip');
  }
  if (ownTurn(now) && !(was !== null && ownTurn(was) && turnKey(was) === turnKey(now))) {
    out.push('sfx-casino-your-turn');
  }
  // The wheel starts to turn on the edge into SPINNING, so the cue is the
  // edge and not the phase: a frame that restates a running spin sounds
  // nothing.
  if (now.phase === CasinoPhase.CASINO_PHASE_SPINNING
    && was?.phase !== CasinoPhase.CASINO_PHASE_SPINNING) {
    out.push('sfx-casino-wheel');
  }
  return out;
}

export interface ResultCueDeps {
  play(id: string): void;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
}

/** The held cues of one result at most. A newer result replaces the cues
 *  that wait. */
export class ResultCue {
  private handles: unknown[] = [];
  private tableId = 0;

  constructor(private readonly deps: ResultCueDeps) {}

  /** Sounds each cue of `r` now or when its picture shows it. */
  onResult(r: CasinoResult, still: boolean): void {
    this.cancel();
    for (const step of resultCuePlan(r, still)) {
      if (step.ms <= 0) { this.deps.play(step.id); continue; }
      const handle: unknown = this.deps.setTimer(() => {
        this.handles = this.handles.filter((h) => h !== handle);
        if (this.handles.length === 0) this.tableId = 0;
        this.deps.play(step.id);
      }, step.ms);
      this.handles.push(handle);
      this.tableId = r.tableId;
    }
  }

  /**
   * The table the reader sits at now, 0 for none. The held cues of a table
   * game whose table is gone are dropped. The slot machine holds no table,
   * so its cues (table 0) are never dropped here.
   */
  onTable(tableId: number): void {
    if (this.handles.length > 0 && this.tableId !== 0 && this.tableId !== tableId) {
      this.cancel();
    }
  }

  /** Drops every held cue. */
  cancel(): void {
    for (const h of this.handles) this.deps.clearTimer(h);
    this.handles = [];
    this.tableId = 0;
  }
}
